import React from "react";
import { View, StyleSheet } from "react-native";
import { SHARE_FORMAT_DIMENSIONS } from "@dramadetector/shared/og";
import type { ShareFormat } from "@dramadetector/shared/og";
import { ShareCardTemplate } from "./ShareCardTemplate";
import { NarratorShareCard } from "./NarratorShareCard";
import { ExpertShareCard } from "./ExpertShareCard";
import { CombinedShareCard } from "./CombinedShareCard";
import type { ShareCardData } from "./ShareCardRenderer";

export interface ShareCardPreviewProps {
	cardData: ShareCardData;
	format?: ShareFormat;
	previewWidth?: number;
	testID?: string;
}

export function ShareCardPreview({
	cardData,
	format = "reel",
	previewWidth = 220,
	testID = "share-card-preview",
}: ShareCardPreviewProps) {
	const dims = SHARE_FORMAT_DIMENSIONS[format];
	const scale = previewWidth / dims.width;
	const previewHeight = dims.height * scale;
	const offsetX = -((dims.width - previewWidth) / 2);
	const offsetY = -((dims.height - previewHeight) / 2);

	const renderCard = () => {
		switch (cardData.type) {
			case "narrator":
				return (
					<ShareCardTemplate backgroundColor="#F5F3FF" format={format}>
						<NarratorShareCard
							episodeTitle={cardData.episodeTitle}
							dramaScore={cardData.dramaScore}
							context={cardData.context}
							testID={`${testID}-narrator-card`}
						/>
					</ShareCardTemplate>
				);
			case "expert":
				return (
					<ShareCardTemplate backgroundColor="#EFF6FF" format={format}>
						<ExpertShareCard
							insightText={cardData.insightText}
							patternTag={cardData.patternTag}
							dramaScore={cardData.dramaScore}
							testID={`${testID}-expert-card`}
						/>
					</ShareCardTemplate>
				);
			case "combined":
				return (
					<ShareCardTemplate backgroundColor="#FFFFFF" format={format}>
						<CombinedShareCard
							episodeTitle={cardData.episodeTitle}
							dramaScore={cardData.dramaScore}
							insightText={cardData.insightText}
							patternTag={cardData.patternTag}
							testID={`${testID}-combined-card`}
						/>
					</ShareCardTemplate>
				);
			default:
				return null;
		}
	};

	return (
		<View
			style={[
				styles.previewFrame,
				{ width: previewWidth, height: previewHeight },
			]}
			testID={testID}
			pointerEvents="none"
		>
			<View
				style={[
					styles.scaledCanvas,
					{
						width: dims.width,
						height: dims.height,
						transform: [
							{ translateX: offsetX },
							{ translateY: offsetY },
							{ scale },
						],
					},
				]}
			>
				{renderCard()}
			</View>
		</View>
	);
}

const styles = StyleSheet.create({
	previewFrame: {
		overflow: "hidden",
		borderRadius: 24,
		backgroundColor: "#FFFFFF",
		borderWidth: 1,
		borderColor: "#E5E7EB",
		alignSelf: "center",
	},
	scaledCanvas: {
		position: "absolute",
		top: 0,
		left: 0,
	},
});
